/**
 * AI Regulatory program API — V98-4-1..4 (`/api/w/:slug/ai-program/*`).
 *
 * Auth is the server's: the legal reviewer (assignment on the AI Regulatory
 * program) decides, asks, records and releases; owner/admin propose; the
 * asked owner answers; every member reads OUTCOMES (privileged fields are
 * never sent to a non-Legal reader — this client never asks for them).
 */
import type {
  CaseLawLinkView,
  DecisionView,
  DeterminationOutcome,
  DeterminationView,
  EuRiskTier,
  EuRole,
  FactQuestionView,
  GridView,
  ObligationSourcesView,
  PrivilegeReleaseView,
  ReleaseRecipientKind,
  CaseLawEffect,
  AiProgramEvent,
  DeterminationsExport,
} from '@aigovops/core';
import { req } from './api';

export type {
  CaseLawLinkView, DecisionView, DeterminationOutcome, DeterminationView, EuRiskTier, EuRole, FactQuestionView, ObligationSourcesView,
  PrivilegeReleaseView, ReleaseRecipientKind, CaseLawEffect, AiProgramEvent, DeterminationsExport,
};

/** The grid as served: each law also carries its case-law links (only while case-law signals are on). */
export type AiGrid = Omit<GridView, 'laws'> & { laws: Array<GridView['laws'][number] & { case_law: CaseLawLinkView[] }> };
export type AiGridCell = AiGrid['cells'][number];
export type AiLaw = AiGrid['laws'][number];

const base = (slug: string) => `/api/w/${encodeURIComponent(slug)}/ai-program`;
const post = <T>(url: string, body: unknown) => req<T>(url, { method: 'POST', body: JSON.stringify(body ?? {}) });

export const getAiGrid = (slug: string) => req<AiGrid>(`${base(slug)}/grid`);
export const getObligationSources = (slug: string) => req<ObligationSourcesView>(`${base(slug)}/sources`);
export const getAiEvents = (slug: string) => req<{ events: AiProgramEvent[] }>(`${base(slug)}/events`);
export const getCellHistory = (slug: string, cellId: string) =>
  req<{ history: DeterminationView[] }>(`${base(slug)}/determinations/${encodeURIComponent(cellId)}/history`);

export interface DraftBody {
  legislation_on?: boolean;
  case_law_on?: boolean;
  jurisdictions?: string[];
  accept_proposal_ids?: string[];
  rationale?: string | null;
  rationale_visibility?: 'legal_only' | 'workspace' | 'external';
  review_by?: string | null;
  triggers?: string[];
}
export const saveSourcesDraft = (slug: string, body: DraftBody) =>
  req<{ draft: unknown }>(`${base(slug)}/sources/draft`, { method: 'PUT', body: JSON.stringify(body) });
export const recordSourcesDecision = (slug: string, body: Pick<DraftBody, 'rationale' | 'rationale_visibility' | 'review_by' | 'triggers'>) =>
  post<{ decision: unknown }>(`${base(slug)}/sources/draft/record`, body);
export const proposeSourcesChange = (slug: string, body: { legislation_on?: boolean; case_law_on?: boolean; jurisdictions?: string[]; note: string }) =>
  post<{ proposal: unknown }>(`${base(slug)}/sources/proposals`, body);
export const declineSourcesProposal = (slug: string, id: string, reason: string) =>
  post<{ proposal: unknown }>(`${base(slug)}/sources/proposals/${encodeURIComponent(id)}/decline`, { reason });

export interface DecideBody {
  outcome: DeterminationOutcome;
  conditions?: string | null;
  eu_role?: EuRole | null;
  eu_risk_tier?: EuRiskTier | null;
  reason: string;
  visibility?: 'legal_only' | 'workspace' | 'external';
}
export const decideCell = (slug: string, cellId: string, body: DecideBody) =>
  req<{ determination: DeterminationView }>(`${base(slug)}/determinations/${encodeURIComponent(cellId)}`, { method: 'PUT', body: JSON.stringify(body) });
export const setInterimPosition = (slug: string, cellId: string, position: string) =>
  post<{ determination: DeterminationView }>(`${base(slug)}/determinations/${encodeURIComponent(cellId)}/interim`, { position });
export const askOwner = (slug: string, cellId: string, question: string, answer?: string) =>
  post<{ question: FactQuestionView }>(`${base(slug)}/determinations/${encodeURIComponent(cellId)}/ask`, answer ? { question, answer } : { question });
export const answerFactQuestion = (slug: string, questionId: string, answer: string) =>
  post<{ question: FactQuestionView }>(`${base(slug)}/fact-questions/${encodeURIComponent(questionId)}/answer`, { answer, attest: true });

export const listPrivilegeReleases = (slug: string) => req<{ releases: PrivilegeReleaseView[] }>(`${base(slug)}/releases`);
export const releaseRationale = (slug: string, body: {
  recipient_kind: ReleaseRecipientKind; recipient_name: string; recipient_email?: string | null; purpose: string;
  determination_ids: string[]; waiver_acknowledged: true; expires_at: string;
}) => post<{ release: PrivilegeReleaseView }>(`${base(slug)}/releases`, body);
export const exportDeterminationsUrl = (slug: string, releaseId?: string | null) =>
  `${base(slug)}/export${releaseId ? `?release_id=${encodeURIComponent(releaseId)}` : ''}`;

export const confirmCaseLawEffect = (slug: string, linkId: string) =>
  post<{ link: CaseLawLinkView }>(`${base(slug)}/case-law-links/${encodeURIComponent(linkId)}/confirm`, {});

// ---- V98-4 review: asks, nudge, withdraw, revoke ----------------------------------------------

export interface AskResult { status: 'asked'; work_item_id: string; to_user_id: string; reused: boolean }
/** T4-B2: a non-Legal member asks the legal reviewer about one cell (the server never echoes Legal's analysis). */
export const askLegalAboutCell = (slug: string, cellId: string, message?: string) =>
  post<AskResult>(`${base(slug)}/determinations/${encodeURIComponent(cellId)}/ask-legal`, message ? { message } : {});
/** T4-H8: ask the org admin to allow AI legislation (the ceiling is frameworks-only). */
export const askOrgAdmin = (slug: string, message?: string) => post<AskResult>(`${base(slug)}/sources/ask-org-admin`, message ? { message } : {});
/** "Not me — suggest someone": the asked owner passes the question on (it is withdrawn; Legal re-asks). */
export const passFactQuestion = (slug: string, questionId: string, body: { suggested_user_id?: string | null; note?: string }) =>
  post<AskResult>(`${base(slug)}/fact-questions/${encodeURIComponent(questionId)}/not-me`, body);
/** T4-B1: Legal nudges the owner while the question is open. */
export const nudgeFactQuestion = (slug: string, questionId: string) =>
  post<{ nudge: { to_user_id: string; for_user_id: string | null } }>(`${base(slug)}/fact-questions/${encodeURIComponent(questionId)}/nudge`, {});
export const withdrawFactQuestion = (slug: string, questionId: string, reason: string) =>
  post<{ question: FactQuestionView }>(`${base(slug)}/fact-questions/${encodeURIComponent(questionId)}/withdraw`, { reason });
/** T4-H7: revoke a release (reason required). */
export const revokeRelease = (slug: string, releaseId: string, reason: string) =>
  post<{ release: PrivilegeReleaseView }>(`${base(slug)}/releases/${encodeURIComponent(releaseId)}/revoke`, { reason });
